import {useCallback, useEffect, useMemo, useState} from 'react'
import {
	autoUpdate,
	flip,
	offset,
	shift,
	size,
	useFloatingNodeId,
	useFloatingParentNodeId,
	useFloating as useFloatingUi,
} from '@floating-ui/react'

const DEFAULT_OFFSET = 4
const BORDER_WIDTH = 1

const throttledAutoUpdate = (ref, float, update) =>
	autoUpdate(ref, float, _.throttle(update, 100), {
		animationFrame: true,
	})

export const useFloating = ({placement, maxHeight, open: controlledOpen, onOpenChange, keepPlacementAfterInitialFlip}) => {
	const nodeId = useFloatingNodeId()
	const isNested = !!useFloatingParentNodeId()
	const [isOpen, setIsOpen] = useState(false)
	const [finalPlacement, setFinalPlacement] = useState(null)

	const shouldFlip = !keepPlacementAfterInitialFlip || finalPlacement === null

	const handleOpenChange = useCallback(
		open => {
			setIsOpen(open)
			onOpenChange?.(open)

			if (!open) setFinalPlacement(null) // reset finalPlacement on close
		},
		[onOpenChange],
	)

	const context = useFloatingUi({
		nodeId,
		open: isOpen,
		onOpenChange: handleOpenChange,
		placement: finalPlacement || placement,
		middleware: [
			offset({
				mainAxis: isNested ? DEFAULT_OFFSET + BORDER_WIDTH : DEFAULT_OFFSET,
				alignmentAxis: isNested ? -BORDER_WIDTH : 0,
			}),
			shouldFlip && flip({padding: 4}),
			// if a maxHeight is passed, we use the size middleware to
			// ensure the floating element is not too big to fit in the viewport
			maxHeight &&
				size({
					apply({availableHeight, elements}) {
						const value = Math.min(maxHeight, Math.floor(availableHeight))
						const {offsetHeight} = elements.floating
						if (offsetHeight < maxHeight || value < offsetHeight) {
							elements.floating.style.maxHeight = `${value}px`
						}
					},
					padding: 4,
				}),
			shift({padding: 4}),
		],
		whileElementsMounted: throttledAutoUpdate,
	})

	const {isPositioned, placement: computedPlacement} = context

	useEffect(() => {
		if (keepPlacementAfterInitialFlip && isPositioned && shouldFlip) {
			setFinalPlacement(computedPlacement)
		}
	}, [isPositioned, computedPlacement, shouldFlip, keepPlacementAfterInitialFlip])

	useEffect(() => {
		setIsOpen(controlledOpen)
	}, [controlledOpen])

	return useMemo(
		() => ({
			...context,
			nodeId,
			isOpen,
			setIsOpen: handleOpenChange,
			isNested,
		}),
		[context, nodeId, isOpen, handleOpenChange, isNested],
	)
}
